import { ReactNode } from 'react';

export function Card({ title, action, children, className = '' }: { title?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={`bg-surface border border-border rounded-sm ${className}`}>
      {title && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-semibold text-ink text-[15px]">{title}</h3>
          {action}
        </div>
      )}
      <div className="p-4">{children}</div>
    </div>
  );
}

export function Button({ children, variant = 'primary', className = '', ...props }: any) {
  const base = 'inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const variants: Record<string, string> = {
    primary: 'bg-primary text-white hover:opacity-90',
    weak: 'border border-border text-ink hover:bg-background',
    link: 'text-primary hover:underline',
    danger: 'border border-red-300 text-red-600 hover:bg-red-50',
  };
  return <button className={`${base} ${variants[variant] || variants.primary} ${className}`} {...props}>{children}</button>;
}

export function Input({ label, className = '', ...props }: any) {
  return (
    <label className="block">
      {label && <span className="mb-1 block text-xs text-muted">{label}</span>}
      <input className={`w-full rounded-sm border border-border bg-white px-2.5 py-1.5 text-sm outline-none focus:border-primary ${className}`} {...props} />
    </label>
  );
}

export function Select({ label, options, className = '', ...props }: any) {
  return (
    <label className="block">
      {label && <span className="mb-1 block text-xs text-muted">{label}</span>}
      <select className={`w-full rounded-sm border border-border bg-white px-2.5 py-1.5 text-sm outline-none focus:border-primary ${className}`} {...props}>
        {options.map((o: any) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

export function Badge({ children, tone = 'gray' }: { children: ReactNode; tone?: string }) {
  const tones: Record<string, string> = {
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    yellow: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
  };
  return <span className={`inline-block rounded-sm px-2 py-0.5 text-xs font-medium ${tones[tone] || tones.gray}`}>{children}</span>;
}

export function Table({ columns, data, empty = '暂无数据' }: { columns: { key: string; title: string; render?: (row: any) => ReactNode }[]; data: any[]; empty?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-muted">
            {columns.map((c) => (
              <th key={c.key} className="px-3 py-2 font-medium whitespace-nowrap">{c.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-muted">{empty}</td></tr>
          ) : (
            data.map((row, i) => (
              <tr key={row.id || i} className="border-b border-border/60 hover:border-l-2 hover:border-l-primary">
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2 whitespace-nowrap">{c.render ? c.render(row) : row[c.key]}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
